import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  localISODate,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Invoice = {
  id: number
  number: string
  partner: number
  partner_name?: string
  invoice_date: string
  due_date: string | null
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'VOID'
  currency_code: string
  total: string
  journal_entry: number | null
  journal_entry_number: string | null
}

type Partner = { id: number; code: string; name: string; partner_type: string; is_active: boolean }

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  number: string
  partner: number | 0
  invoice_date: string
  due_date: string | null
  currency_code: string
}

const STATUS_COLOR: Record<Invoice['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  OPEN: 'bg-blue-100 text-blue-800',
  PAID: 'bg-green-100 text-green-800',
  VOID: 'bg-red-100 text-red-700',
}

const STATUS_FILTERS: ('ALL' | Invoice['status'])[] = ['ALL', 'DRAFT', 'OPEN', 'PAID', 'VOID']

export default function FinanceInvoices() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const invoicesQ = useQuery({
    queryKey: ['finance', 'invoices'],
    queryFn: async () => (await api.get<Invoice[]>('/finance/invoices/')).data,
  })
  const partnersQ = useQuery({
    queryKey: ['finance', 'partners'],
    queryFn: async () => (await api.get<Partner[]>('/partners/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterStatus, setFilterStatus] = useState<'ALL' | Invoice['status']>('ALL')

  function openCreate() {
    setDraft({
      ...emptyDraft(),
      number: `INV-${Date.now().toString().slice(-6)}`,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Invoice>('/finance/invoices/', {
      ...draft,
      partner: Number(draft.partner),
      status: 'DRAFT',
      notes: '',
      lines: [],
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      toast.success('Invoice created')
      setCreating(false)
      navigate(`/admin/finance/invoices/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/finance/invoices/${id}/`),
    onSuccess: () => {
      toast.success('Invoice deleted')
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const postMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/finance/invoices/${id}/post_to_gl/`)).data,
    onSuccess: () => {
      toast.success('Posted to General Ledger')
      qc.invalidateQueries({ queryKey: ['finance', 'invoices'] })
      qc.invalidateQueries({ queryKey: ['finance', 'journal-entries'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const invoices = invoicesQ.data || []
  const partners = (partnersQ.data || []).filter(
    (p) => p.is_active && (p.partner_type === 'CUSTOMER' || p.partner_type === 'BOTH')
  )
  const filtered = filterStatus === 'ALL'
    ? invoices
    : invoices.filter((i) => i.status === filterStatus)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Invoices</h2>
          <p className="text-sm text-muted-foreground">
            {invoices.length} total · {invoices.filter((i) => i.status === 'OPEN').length} open
          </p>
        </div>
        <Button onClick={openCreate} disabled={partners.length === 0}>
          <Plus className="h-3 w-3 mr-1" /> New invoice
        </Button>
      </div>

      {partners.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3 rounded-lg mb-4">
          Add at least one active <strong>customer</strong> under Partners before creating invoices.
        </div>
      )}

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Status:</span>
        {STATUS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1 rounded ${filterStatus === s ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {invoicesQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">
          No invoices.
        </div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Number</Th>
                <Th>Date</Th>
                <Th>Customer</Th>
                <Th>Status</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((inv) => (
                <tr
                  key={inv.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/finance/invoices/${inv.id}`)}
                >
                  <Td className="font-mono text-xs">{inv.number}</Td>
                  <Td className="text-xs">{inv.invoice_date}</Td>
                  <Td>{inv.partner_name}</Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[inv.status]}`}>{inv.status}</span>
                      {inv.journal_entry_number && (
                        <span className="text-xs text-muted-foreground font-mono">{inv.journal_entry_number}</span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/finance/invoices/${inv.id}`)}>
                          Open
                        </DropdownMenuItem>
                        {inv.status === 'DRAFT' && !inv.journal_entry && (
                          <DropdownMenuItem
                            onClick={() => confirm(
                              `Post invoice ${inv.number} to General Ledger`,
                              () => postMut.mutate(inv.id),
                            )}
                            disabled={postMut.isPending}
                            className="text-green-700"
                          >
                            Post to GL
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!!inv.journal_entry}
                          onClick={() => !inv.journal_entry && confirm(
                            `invoice ${inv.number}`,
                            () => deleteMut.mutate(inv.id),
                          )}
                          className={!inv.journal_entry ? 'text-destructive' : ''}
                        >
                          {inv.journal_entry ? 'Delete (posted)' : 'Delete'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Lines + notes filled in on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New invoice"
        description="Create the invoice header. Add line items, notes, and post to the General Ledger on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="iv-num">Number *</FieldLabel>
              <Input id="iv-num" autoFocus required value={draft.number}
                onChange={(ev) => setDraft({ ...draft, number: ev.target.value })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="iv-cur">Currency</FieldLabel>
              <Input id="iv-cur" value={draft.currency_code}
                onChange={(ev) => setDraft({ ...draft, currency_code: ev.target.value.toUpperCase().slice(0, 3) })}
                className="font-mono" maxLength={3} />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="iv-customer">Customer *</FieldLabel>
            <SearchableSelect
              id="iv-customer"
              value={draft.partner ? String(draft.partner) : undefined}
              onChange={(v) => setDraft({ ...draft, partner: Number(v) })}
              placeholder="— select —"
              options={partners.map((p) => ({
                value: String(p.id), label: p.name, hint: p.code,
              }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="iv-date">Invoice date *</FieldLabel>
              <DatePicker id="iv-date" required value={draft.invoice_date}
                onChange={(v) => setDraft({ ...draft, invoice_date: v })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="iv-due">Due date</FieldLabel>
              <DatePicker id="iv-due" value={draft.due_date || ''}
                onChange={(v) => setDraft({ ...draft, due_date: v || null })} />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    number: '',
    partner: 0,
    invoice_date: localISODate(new Date()),
    due_date: null,
    currency_code: 'EUR',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
